import { FormControl, FormControlLabel } from "@commonlibs";
import clsx from "clsx";
import AddIcon from "ui/common/icons/AddIcon";
import useUploadFile from "features/file/hooks/useUploadFile";
import { FileUri } from "../FileUpload/FileUpload.types";
import { MultipleImageUploadWrapper } from "./ImageUpload.styles";
import { ImageUploadProps } from "./ImageUpload.types";
import SingleImageUpload from "./SingleImageUpload/SingleImageUpload";

function ImageUpload<Multiple extends boolean = false>({
    //Image input props
    showControllers,
    controllersPosition,
    showLoading,
    multiple,
    fullWidth,
    size,
    onSuccess,
    onError,
    alt,
    isLoading,
    changeButtonProps,
    uploadButtonProps,
    addMoreButtonProps = { Icon: AddIcon },
    showDelete = true,
    uploadPath = "image",
    uploadFn,
    //Common props
    readonly,
    disabled,
    showValid,
    hideError,
    //FormControl props
    Label = FormControlLabel,
    className,
    defaultValue,
    ...rest
}: ImageUploadProps<Multiple>): JSX.Element {
    const { mutateAsync: defaultUpload } = useUploadFile(uploadPath);
    const mutateAsync = uploadFn ?? defaultUpload;

    const uploadProps = {
        upload: mutateAsync,
        showControllers,
        controllersPosition,
        showLoading,
        fullWidth,
        size,
        onSuccess,
        onError,
        alt,
        readonly,
        disabled,
        showValid,
        hideError,
        isLoading,
        changeButtonProps,
        showDelete,
        uploadButtonProps,
    };

    return (
        <FormControl
            className={clsx("image-upload", className)}
            Label={Label}
            readonly={readonly}
            disabled={disabled}
            showValid={showValid}
            hideError={hideError}
            defaultValue={defaultValue}
            css={{ placeSelf: "unset" }}
            {...rest}
        >
            {(value, setValue) => {
                if (!multiple) return <SingleImageUpload {...uploadProps} value={value} onChange={setValue} />;

                const handleChange = (file: string | undefined, index: number) => {
                    if (file) {
                        //Change the file in the correct position
                        setValue((value as string[]).map((f, i) => (i === index ? file : f)) as FileUri<Multiple>);
                    } else {
                        //Remove the file from the array
                        setValue((value as string[]).filter((_, i) => i !== index) as FileUri<Multiple>);
                    }
                };

                const handleMultipleChange = (file: string[] | undefined = []) => {
                    setValue([...(value || []), ...file] as FileUri<Multiple>);
                };

                return (
                    <MultipleImageUploadWrapper fullWidth={fullWidth}>
                        {Array.isArray(value) &&
                            value.map((file, index) => (
                                <SingleImageUpload<false>
                                    {...uploadProps}
                                    value={file}
                                    onChange={(f) => handleChange(f, index)}
                                    key={index} // skipcq: JS-0437
                                    multiple={false}
                                />
                            ))}
                        <SingleImageUpload<true>
                            {...uploadProps}
                            uploadButtonProps={{ ...uploadButtonProps, ...(value && addMoreButtonProps) }}
                            onChange={handleMultipleChange}
                            multiple
                        />
                    </MultipleImageUploadWrapper>
                );
            }}
        </FormControl>
    );
}

export default ImageUpload;
